<script setup>
definePage({
  meta: {
    action: 'view',
    subject: 'log',
  },
})

import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { logService } from '@/utils/services/logService'

const { t } = useI18n()

const logs = ref([])
const loading = ref(false)
const selectedLog = ref(null)
const showModal = ref(false)

// Фильтры
const filters = ref({
  type: '',
  level: '',
  search: '',
  per_page: 20
})

// Пагинация
const pagination = ref({
  current_page: 1,
  last_page: 1,
  per_page: 20,
  total: 0
})

// Опции для фильтров
const logTypes = ref([])
const logLevels = ref([])

const levelColors = {
  'info': 'success',
  'warning': 'warning',
  'error': 'error',
  'debug': 'info'
}

const levelIcons = {
  'info': 'tabler-info-circle',
  'warning': 'tabler-alert-triangle',
  'error': 'tabler-alert-circle',
  'debug': 'tabler-bug'
}

const fetchLogs = async () => {
  loading.value = true
  try {
    const params = {
      ...filters.value,
      page: pagination.value.current_page
    }

    const response = await logService.getAll(params)
    logs.value = response.data
    pagination.value = response.pagination
  } catch (error) {
    console.error(t('logs.error_fetching_logs'), error)
  } finally {
    loading.value = false
  }
}

const fetchFilters = async () => {
  try {
    const [typesResponse, levelsResponse] = await Promise.all([
      logService.getTypes(),
      logService.getLevels()
    ])

    logTypes.value = typesResponse.data
    logLevels.value = levelsResponse.data
  } catch (error) {
    console.error(t('logs.error_fetching_filters'), error)
  }
}

const viewLog = async (log) => {
  try {
    const response = await logService.getById(log.id)
    selectedLog.value = response.data
    showModal.value = true
  } catch (error) {
    console.error(t('logs.error_fetching_log_details'), error)
  }
}

const clearFilters = () => {
  filters.value = {
    type: '',
    level: '',
    search: '',
    per_page: 20
  }
  pagination.value.current_page = 1
  fetchLogs()
}

const applyFilters = () => {
  pagination.value.current_page = 1
  fetchLogs()
}

const changePage = (page) => {
  pagination.value.current_page = page
  fetchLogs()
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString()
}

const truncateMessage = (message, length = 100) => {
  return message.length > length ? message.substring(0, length) + '...' : message
}

onMounted(() => {
  fetchFilters()
  fetchLogs()
})
</script>

<template>
  <div>
    <VCard>
      <VCardTitle class="d-flex align-center justify-space-between">
        <span>{{ $t('logs.title') }}</span>
        <VBtn v-if="$can('view', 'log')" color="primary" @click="fetchLogs" :loading="loading">
          <VIcon start icon="tabler-refresh" />
          {{ $t('logs.refresh') }}
        </VBtn>
      </VCardTitle>

      <VCardText>
        <!-- Фильтры -->
        <VRow v-if="$can('view', 'log')">
          <VCol cols="12" md="3">
            <VSelect v-model="filters.type" :items="logTypes" :label="$t('logs.filter_by_type')" clearable
              variant="outlined" density="compact" />
          </VCol>
          <VCol cols="12" md="3">
            <VSelect v-model="filters.level" :items="logLevels" :label="$t('logs.filter_by_level')" clearable
              variant="outlined" density="compact" />
          </VCol>
          <VCol cols="12" md="3">
            <VTextField v-model="filters.search" :label="$t('logs.search')" variant="outlined" density="compact"
              clearable @keyup.enter="applyFilters" />
          </VCol>
        </VRow>

        <VRow v-if="$can('view', 'log')" class="mb-4">

          <VCol cols="12" md="3" class="d-flex gap-2">
            <VBtn color="primary" @click="applyFilters" :loading="loading">
              {{ $t('logs.apply_filters') }}
            </VBtn>
            <VBtn variant="outlined" @click="clearFilters">
              {{ $t('logs.clear_filters') }}
            </VBtn>
          </VCol>
        </VRow>

        <!-- Таблица логов -->
        <VTable v-if="!loading && $can('view', 'log')">
          <thead>
            <tr>
              <th>{{ $t('logs.id') }}</th>
              <th>{{ $t('logs.type') }}</th>
              <th>{{ $t('logs.level') }}</th>
              <th>{{ $t('logs.message') }}</th>
              <th>{{ $t('logs.created_at') }}</th>
              <th>{{ $t('logs.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in logs" :key="log.id">
              <td>{{ log.id }}</td>
              <td>
                <VChip :color="log.type === 'currency' ? 'primary' : 'default'" size="small">
                  {{ log.type }}
                </VChip>
              </td>
              <td>
                <VChip :color="levelColors[log.level] || 'default'" size="small">
                  <VIcon start :icon="levelIcons[log.level] || 'tabler-circle'" size="16" />
                  {{ log.level }}
                </VChip>
              </td>
              <td>
                <span class="text-truncate">
                  {{ truncateMessage(log.message) }}
                </span>
              </td>
              <td>
                <span>
                  {{ formatDate(log.created_at) }}
                </span>
              </td>
              <td>
                <VBtn v-if="$can('view', 'log')" size="small" variant="outlined" @click="viewLog(log)">
                  <VIcon start icon="tabler-eye" size="16" />
                  {{ $t('logs.view') }}
                </VBtn>
              </td>
            </tr>
          </tbody>
        </VTable>

        <VSkeletonLoader v-else-if="loading" type="table" />

        <VAlert v-else-if="!$can('view', 'log')" type="warning" variant="tonal">
          {{ $t('logs.no_permission') }}
        </VAlert>

        <!-- Пагинация -->
        <div v-if="$can('view', 'log')" class="d-flex justify-center mt-4">
          <VPagination v-model="pagination.current_page" :length="pagination.last_page" :total-visible="7"
            @update:model-value="changePage" />
        </div>
      </VCardText>
    </VCard>

    <!-- Модальное окно для детального просмотра -->
    <VDialog v-if="$can('view', 'log')" v-model="showModal" max-width="800px">
      <VCard v-if="selectedLog">
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>{{ $t('logs.log_details') }}</span>
          <VBtn icon variant="text" @click="showModal = false">
            <VIcon icon="tabler-x" />
          </VBtn>
        </VCardTitle>

        <VCardText>
          <VRow>
            <VCol cols="12" md="6">
              <VTextField :model-value="selectedLog.id" :label="$t('logs.id')" readonly variant="outlined"
                density="compact" />
            </VCol>
            <VCol cols="12" md="6">
              <VTextField :model-value="selectedLog.type" :label="$t('logs.type')" readonly variant="outlined"
                density="compact" />
            </VCol>
            <VCol cols="12" md="6">
              <VTextField :model-value="selectedLog.level" :label="$t('logs.level')" readonly variant="outlined"
                density="compact" />
            </VCol>
            <VCol cols="12" md="6">
              <VTextField :model-value="formatDate(selectedLog.created_at)" :label="$t('logs.created_at')" readonly
                variant="outlined" density="compact" />
            </VCol>
            <VCol cols="12">
              <VTextField :model-value="selectedLog.message" :label="$t('logs.message')" readonly variant="outlined"
                density="compact" rows="3" />
            </VCol>
            <VCol cols="12">
              <VCard variant="outlined">
                <VCardTitle class="text-h6">
                  {{ $t('logs.context') }}
                </VCardTitle>
                <VCardText>
                  <pre class="text-body-2"
                    style="overflow-x: auto; white-space: pre-wrap; word-wrap: break-word;">{{ JSON.stringify(selectedLog.context, null, 2) }}</pre>
                </VCardText>
              </VCard>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>